import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { ChatMessage } from "./editor-types"
import type { SessionType } from "@/types"

import { MarkdownRenderer } from "@/components/markdown-renderer"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { ChatFooter } from "@/features/chat/components/chat-footer"
import { SystemMessageLayout } from "@/features/chat/components/system-message-layout"
import { UserMessage } from "@/features/chat/components/user-message"
import styles from "@/features/chat/styles/answer.module.css"
import { GenerationProgress } from "@/features/studio-ai/components/generation-progress"
import { useStudioImageThumbnail } from "@/features/studio-ai/hooks/use-studio-image-thumbnail"
import { useStudioAIStore } from "@/stores/studio-ai-store"

interface EditorChatPanelProps {
  messages: ChatMessage[]
  isProcessing: boolean
  onApply: (prompt: string) => void
  /** Receives (legacyUrl, jobId) so the canvas can resolve through the
   *  same batch-prefetch cache the chat used for rendering. */
  onImageClick?: (imageUrl: string, jobId: string | undefined) => void
  sessionType: SessionType
}

/**
 * Renders a chat message thumbnail through the batch-prefetch cache. Lifted
 * into a child component so we can call `useStudioImageThumbnail` per
 * message — calling it inline in a .map() would violate the rules of hooks.
 */
function ChatMessageThumbnail({
  imageUrl,
  imageJobId,
  alt,
  onClick,
}: {
  imageUrl: string
  imageJobId: string | undefined
  alt: string
  onClick: () => void
}) {
  const { url, error, onImgError } = useStudioImageThumbnail(imageJobId, imageUrl)
  if (!url || error) return null
  return (
    <button type="button" onClick={onClick}>
      <img alt={alt} className="max-h-48 rounded object-contain" src={url} onError={onImgError} />
    </button>
  )
}

export function EditorChatPanel({
  messages,
  isProcessing,
  onApply,
  onImageClick,
  sessionType,
}: EditorChatPanelProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.imageEditor" })
  const endRef = useRef<HTMLDivElement>(null)
  // The editor pipes partial-image bytes into the studio-ai store via
  // updateGenerationState in image-editor-view.tsx, but no GenerationProgress
  // card was ever mounted in the editor — only in ImageEditForm and
  // ImageWizard. So edits ran with no visible streaming preview. Mirror the
  // same render pattern those two surfaces use, so the user sees the partial
  // frames refining from 50% → 75% → 90% during a long edit.
  //
  // Gate on isGenerating, NOT on partialImageUrl. The editor's post-edit
  // handler intentionally keeps partialImageUrl set (to the final result URL)
  // so other surfaces can keep showing the final frame — but the chat panel
  // already shows that frame via the assistant message thumbnail. If we keyed
  // off partialImageUrl too, the progress card would linger next to every
  // completed-edit message for the rest of the session.
  const generationState = useStudioAIStore((state) => state.generationState)
  const showProgressCard = isProcessing || generationState.isGenerating

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, isProcessing, generationState.isGenerating])

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-4 py-3">
        <h3 className="text-sm font-semibold">{t("chatTitle")}</h3>
        <p className="text-muted-foreground text-xs">{t("chatDescription")}</p>
      </div>
      <Separator className="mx-4 w-auto" />

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-6 p-4">
          {messages.length === 0 && (
            <p className="text-muted-foreground text-center text-sm">{t("chatEmpty")}</p>
          )}

          {messages.map((msg) => {
            if (msg.role === "user") {
              return <UserMessage key={msg.id} message={msg.content} />
            }

            return (
              <SystemMessageLayout key={msg.id}>
                <SystemMessageLayout.Content>
                  <MarkdownRenderer
                    className="prose-sm max-w-full! wrap-anywhere"
                    content={msg.content}
                  />
                  {msg.imageUrl && (
                    <ChatMessageThumbnail
                      alt={t("editResult")}
                      imageJobId={msg.imageJobId}
                      imageUrl={msg.imageUrl}
                      onClick={() => onImageClick?.(msg.imageUrl!, msg.imageJobId)}
                    />
                  )}
                </SystemMessageLayout.Content>
              </SystemMessageLayout>
            )
          })}

          {/* Live partial-image preview while the edit is in flight. Same
              GenerationProgress component used by ImageEditForm/ImageWizard,
              so all three surfaces show streaming partials consistently. */}
          {showProgressCard && (
            <SystemMessageLayout>
              <SystemMessageLayout.Content>
                <GenerationProgress
                  jobId={generationState.currentJobId ?? undefined}
                  showEstimate={false}
                />
                {isProcessing && (
                  <div className="text-muted-foreground flex items-center gap-2 py-2 text-sm">
                    <span className={styles.loadingdots}>{t("processing")}</span>
                  </div>
                )}
              </SystemMessageLayout.Content>
            </SystemMessageLayout>
          )}

          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Footer */}
      <ChatFooter
        clearOnSend
        disableFileSelection
        availableFiles={[]}
        changeTokenCounts={() => {}}
        disableSend={isProcessing}
        enableHighReasoning={false}
        maxTokens={128000}
        placeholder={t("chatPlaceholder")}
        sessionType={sessionType}
        showCancelOption={false}
        showChatOptions={false}
        showHighReasoning={false}
        totalTokenCount={0}
        onCancel={() => {}}
        onFilesAdded={() => {}}
        onGetToggleVoiceMode={() => {}}
        onHighReasoningChange={() => {}}
        onRemoveFile={() => {}}
        onSend={onApply}
        onSetQuestion={() => {}}
      />
    </div>
  )
}
